/**
 * Bucket the threads index by `updatedAt` into the five day-buckets
 * the Taqat sidebar uses:
 *
 *   - Today        (same calendar day as now, local time)
 *   - Yesterday    (one calendar day before now)
 *   - Last 7 days  (within the last 7 days, excluding today/yesterday)
 *   - Last 30 days (within the last 30 days, excluding above)
 *   - Older        (everything else)
 *
 * Within each bucket the order is preserved from the input array —
 * the `/api/ai/threads` endpoint already returns threads sorted by
 * `updatedAt DESC` so we don't re-sort here.
 *
 * Empty buckets are omitted from the result so the sidebar doesn't
 * render dead group headers.
 */
import type { ThreadSummary } from "./types.ts";

export type ThreadGroupKey = "today" | "yesterday" | "lastWeek" | "lastMonth" | "older";

export interface ThreadGroup {
  key: ThreadGroupKey;
  label: string;
  threads: ThreadSummary[];
}

const GROUP_LABELS: Record<ThreadGroupKey, string> = {
  today: "Today",
  yesterday: "Yesterday",
  lastWeek: "Last 7 days",
  lastMonth: "Last 30 days",
  older: "Older",
};

const GROUP_ORDER: ThreadGroupKey[] = ["today", "yesterday", "lastWeek", "lastMonth", "older"];

/**
 * Compute the start-of-day midnight for the given Date in local time.
 * `Date.UTC` is intentionally not used — bucket boundaries should
 * match the user's calendar day, not UTC.
 */
function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function groupThreadsByDay(threads: ThreadSummary[], now: Date = new Date()): ThreadGroup[] {
  const todayStart = startOfLocalDay(now);
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgoStart = todayStart - 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgoStart = todayStart - 30 * 24 * 60 * 60 * 1000;

  const buckets: Record<ThreadGroupKey, ThreadSummary[]> = {
    today: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  };

  for (const thread of threads) {
    const updated = new Date(thread.updatedAt).getTime();
    if (updated >= todayStart) {
      buckets.today.push(thread);
    } else if (updated >= yesterdayStart) {
      buckets.yesterday.push(thread);
    } else if (updated >= sevenDaysAgoStart) {
      buckets.lastWeek.push(thread);
    } else if (updated >= thirtyDaysAgoStart) {
      buckets.lastMonth.push(thread);
    } else {
      buckets.older.push(thread);
    }
  }

  return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: GROUP_LABELS[key],
    threads: buckets[key],
  }));
}
